var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import { Text } from '@inter-ikea-kompis/component-text';
import { FocusRing, Hyperlink } from '@ingka/common-styles-webc';
import { drill, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import AssemblyServicesTracker from '../../trackers/AssemblyServicesTracker.js';
import AssemblyServicesStatusButton from '../assembly-services-status-button/AssemblyServicesStatusButton.js';
import AssemblyServicesStyle from './AssemblyServicesStyle.js';
/**
 * @event assemblyServicesClick User clicks on the assembly services status.
 */
export default class AssemblyServices extends KompisElement {
    constructor() {
        super(...arguments);
        // Assembly services data, containing if assembly services are available or not.
        this.assemblyServicesData = null;
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Submitted and validated ZIP code.
        this.zipCode = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Used for automatic analytics. If null or not set, analytics is turned off.
        this.analytics = null;
        this.tracker = null;
        this.dexfSettings = null;
    }
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${Hyperlink.CSS + FocusRing.CSS}
				${AssemblyServicesStyle.getStyle({ theme: this.theme })}
			</style>

			${this.getIcon()}

			<div>${this.getTitle()} ${this.getAssemblyServicesStatusButton()} ${this.getInfoLink()}</div>
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.disconnectTracker();
    }
    async connectedCallback() {
        registerIcon(drill);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-assembly-services-status-button', AssemblyServicesStatusButton);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('analytics') && this.hasTranslationsAndSettings()) {
            this.disconnectTracker();
            this.connectTracker();
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getSettings() {
        return this.dexfSettings;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    async connectTracker() {
        if (this.analytics) {
            this.tracker = new AssemblyServicesTracker({
                ...this.analytics,
                settings: this.getSettings()
            });
            await this.tracker?.connect();
            // Make sure child components with analytics connect their trackers when the id is available.
            this.requestUpdate();
        }
    }
    disconnectTracker() {
        this.tracker?.disconnect();
        this.tracker = null;
    }
    getIcon() {
        return html ` <skapa-icon icon="drill"></skapa-icon> `;
    }
    getTitle() {
        return html `<h3>${this.getTranslations().assemblyServicesTitle}</h3>`;
    }
    getAssemblyServicesStatusButton() {
        return html `<kompis-assembly-services-status-button
			.theme="${this.theme}"
			.translations="${this.translations}"
			.assemblyServicesData="${this.assemblyServicesData}"
			.zipCode="${this.zipCode}"
			.localizedInformation="${this.localizedInformation}"
			@statusButtonClick="${() => {
            this.dispatchEvent(new CustomEvent('assemblyServicesClick', {
                bubbles: true
            }));
            this.sendAssemblyServicesClick();
        }}"
		></kompis-assembly-services-status-button>`;
    }
    getInfoLink() {
        const url = this.getSettings().kompis.urls.assemblyServicesLink;
        if (!url || this.disableLinks || !this.assemblyServicesData || !this.zipCode) {
            return null;
        }
        return html `
			<a
				class="${Hyperlink.classnames()}"
				href="${url}"
				target="_blank"
				@click="${() => this.tracker?.sendLearnMoreNavigation()}"
				>${this.getTranslations().assemblyServicesLearnMore}</a
			>
		`;
    }
    sendAssemblyServicesClick() {
        this.tracker?.sendAssemblyServicesClick();
    }
}
__decorate([
    property({ type: Object, required: true })
], AssemblyServices.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], AssemblyServices.prototype, "assemblyServicesData", void 0);
__decorate([
    property({ type: Object })
], AssemblyServices.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], AssemblyServices.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], AssemblyServices.prototype, "settings", void 0);
__decorate([
    property({ type: String })
], AssemblyServices.prototype, "zipCode", void 0);
__decorate([
    property({ type: Boolean })
], AssemblyServices.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Object })
], AssemblyServices.prototype, "analytics", void 0);
//# sourceMappingURL=AssemblyServices.js.map